import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaUser, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCalendarAlt, FaShoppingCart, FaCreditCard, FaTruck, FaFileAlt } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { fetchOrderDetails, clearOrderDetails } from '../../../redux/Slices/AdminOrdersSlice';

const AdminOrderDetails = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();

  const { orderDetails, detailsLoading, detailsError } = useSelector(state => state.adminOrders);

  useEffect(() => {
    setTitle('تفاصيل الأوردر');
    dispatch(clearOrderDetails());
    if (orderId) {
      dispatch(fetchOrderDetails(orderId));
    }
  }, [orderId]);

  const getStatusBadge = (status) => {
    const statusMap = {
      'placed': { text: 'تم الطلب', class: 'bg-primary' },
      'confirmed': { text: 'مؤكد', class: 'bg-info' },
      'preparing': { text: 'قيد التحضير', class: 'bg-warning' },
      'ready': { text: 'جاهز', class: 'bg-success' },
      'completed': { text: 'مكتمل', class: 'bg-success' },
      'cancelled': { text: 'ملغي', class: 'bg-danger' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white fs-6`}>
        {statusInfo.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'في الانتظار', class: 'bg-warning' },
      'paid': { text: 'مدفوع', class: 'bg-success' },
      'failed': { text: 'فشل', class: 'bg-danger' },
      'refunded': { text: 'مسترد', class: 'bg-info' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white fs-6`}>
        {statusInfo.text}
      </span>
    );
  };

  const getDeliveryStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'في الانتظار', class: 'bg-warning' },
      'assigned': { text: 'تم التخصيص', class: 'bg-info' },
      'accepted': { text: 'مقبول', class: 'bg-success' },
      'picked_up': { text: 'تم الاستلام', class: 'bg-primary' },
      'on_way': { text: 'في الطريق', class: 'bg-warning' },
      'delivered': { text: 'تم التسليم', class: 'bg-success' },
      'failed': { text: 'فشل التسليم', class: 'bg-danger' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white fs-6`}>
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'غير محدد';
    return `${price} ج.م`;
  };

  if (detailsLoading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (detailsError) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {detailsError}
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          لم يتم العثور على تفاصيل الأوردر
        </div>
      </div>
    );
  }

  return (
    <div className="main_content">
      <div className="dashboard-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="d-flex justify-content-between align-items-center mb-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-outline-secondary"
            style={{
              borderColor: 'rgb(24, 65, 148)',
              color: 'rgb(24, 65, 148)'
            }}
            onClick={() => navigate('/admin/orders')}
          >
            <FaArrowLeft className="mx-2" />
            العودة للقائمة
          </motion.button>
          <h1 style={{fontSize: '20px'}} className="fw-bold text-dark mb-0">تفاصيل الأوردر #{orderDetails.id}</h1>
        </motion.div>

        <div className="row">
          {/* Order Info */}
          <div className="col-md-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="card shadow-sm mb-4"
            >
              <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                <h5 className="mb-0 d-flex align-items-center">
                  <FaShoppingCart className="mx-2" />
                  معلومات الأوردر
                </h5>
              </div>
              <div className="card-body p-4">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">حالة الطلب:</label>
                        <div>{getStatusBadge(orderDetails.status)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">حالة الدفع:</label>
                        <div>{getPaymentStatusBadge(orderDetails.payment_status)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">حالة التوصيل:</label>
                        <div>{getDeliveryStatusBadge(orderDetails.delivery_status)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">تاريخ الطلب:</label>
                        <div className="d-flex align-items-center">
                          <FaCalendarAlt className="mx-2 text-primary" />
                          <span>{formatDate(orderDetails.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">طريقة الدفع:</label>
                        <div className="d-flex align-items-center">
                          <FaCreditCard className="mx-2 text-primary" />
                          <span>{orderDetails.payment_method || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">طريقة التوصيل:</label>
                        <div className="d-flex align-items-center">
                          <FaTruck className="mx-2 text-primary" />
                          <span>{orderDetails.delivery_method || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label fw-bold">الملاحظات:</label>
                        <div className="d-flex align-items-center">
                          <FaFileAlt className="mx-2 text-primary" />
                          <span>{orderDetails.notes || 'لا توجد ملاحظات'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

            {/* Client Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="card shadow-sm mb-4"
            >
              <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                <h5 className="mb-0 d-flex align-items-center">
                  <FaUser className="mx-2" />
                  معلومات العميل
                </h5>
              </div>
              <div className="card-body p-4">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">الاسم:</label>
                        <div className="d-flex align-items-center">
                          <FaUser className="mx-2 text-primary" />
                          <span>{orderDetails.client?.name || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">الهاتف:</label>
                        <div className="d-flex align-items-center">
                          <FaPhone className="mx-2 text-primary" />
                          <span>{orderDetails.client?.phone || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">البريد الإلكتروني:</label>
                        <div className="d-flex align-items-center">
                          <FaEnvelope className="mx-2 text-primary" />
                          <span>{orderDetails.client?.email || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label fw-bold">العنوان:</label>
                        <div className="d-flex align-items-center">
                          <FaMapMarkerAlt className="mx-2 text-primary" />
                          <span>{orderDetails.client?.address || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

            {/* Delivery Info */}
            {orderDetails.delivery && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
                className="card shadow-sm mb-4"
              >
                <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaTruck className="mx-2" />
                    معلومات المندوب
                  </h5>
                </div>
                <div className="card-body p-4">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">الاسم:</label>
                          <div className="d-flex align-items-center">
                            <FaUser className="mx-2 text-primary" />
                            <span>{orderDetails.delivery?.name || 'غير محدد'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">الهاتف:</label>
                          <div className="d-flex align-items-center">
                            <FaPhone className="mx-2 text-primary" />
                            <span>{orderDetails.delivery?.phone || 'غير محدد'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">البريد الإلكتروني:</label>
                          <div className="d-flex align-items-center">
                            <FaEnvelope className="mx-2 text-primary" />
                            <span>{orderDetails.delivery?.email || 'غير محدد'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">رقم المركبة:</label>
                          <div className="d-flex align-items-center">
                            <FaTruck className="mx-2 text-primary" />
                            <span>{orderDetails.delivery?.vehicle_number || 'غير محدد'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
            )}

            {/* Items */}
            {orderDetails.items && orderDetails.items.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="card shadow-sm mb-4"
              >
                <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                  <h5 className="mb-0 d-flex align-items-center">
                    <FaFileAlt className="mx-2" />
                    عناصر الأوردر
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="table-responsive">
                    <table className="table mb-0" style={{ backgroundColor: 'transparent' }}>
                      <thead>
                        <tr>
                          <th className="border-0 fw-semibold text-center">#</th>
                          <th className="border-0 fw-semibold text-center">اسم المنتج</th>
                          <th className="border-0 fw-semibold text-center">الكمية</th>
                          <th className="border-0 fw-semibold text-center">السعر</th>
                          <th className="border-0 fw-semibold text-center">المجموع</th>
                          <th className="border-0 fw-semibold text-center">ملاحظات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetails.items.map((item, index) => (
                          <tr key={item.id} style={{ backgroundColor: 'transparent' }}>
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center">
                              <div className="fw-medium">{item.product_name || 'غير محدد'}</div>
                              {item.meta?.unit_name && (
                                <small className="text-muted">({item.meta.unit_name})</small>
                              )}
                            </td>
                            <td className="text-center">{item.quantity || 'غير محدد'}</td>
                            <td className="text-center">{formatPrice(item.price)}</td>
                            <td className="text-center fw-bold text-success">{formatPrice(item.line_total)}</td>
                            <td className="text-center">{item.meta?.note || 'لا توجد ملاحظات'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}
            </div>

          {/* Order Summary */}
          <div className="col-md-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="card shadow-sm mb-4"
            >
              <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                <h5 className="mb-0 d-flex align-items-center">
                  <FaCreditCard className="mx-2" />
                  ملخص الأوردر
                </h5>
              </div>
              <div className="card-body p-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>المجموع الفرعي:</span>
                    <span className="fw-bold">{formatPrice(orderDetails.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>الخصم:</span>
                    <span className="fw-bold text-success">-{formatPrice(orderDetails.discount_total)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>رسوم التوصيل:</span>
                    <span className="fw-bold">{formatPrice(orderDetails.delivery_fees)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>رسوم إضافية:</span>
                    <span className="fw-bold">{formatPrice(orderDetails.extra_fees)}</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mb-3">
                    <span className="fs-5 fw-bold">المجموع الكلي:</span>
                    <span className="fs-5 fw-bold text-success">{formatPrice(orderDetails.total)}</span>
                  </div>
                  
                  <div className="mt-4">
                  
                    <div className="mb-2">
                      <label className="form-label fw-bold">تاريخ الإنشاء:</label>
                      <div className="d-flex align-items-center">
                        <FaCalendarAlt className="mx-2 text-primary" />
                        <span>{formatDate(orderDetails.created_at)}</span>
                      </div>
                    </div>
                    
                  </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetails;
